import type { Destination } from '../../../types/mappings'

/**
 * In-memory registry of `Destination` entries indexed by the combined slug.
 * Populated at load time via `loadMappings()` and queried by destinations at
 * init.
 *
 * The key is `${slug}-${version}` (e.g. `insider-default-v1`) — the same
 * string the user configures via `destinations.insider.slug`, the SDK echoes
 * verbatim to rum-api in `?slugs=`. The DB and the wire keep slug/version
 * split (`destinations.slug` + `destinations.version`); the SDK is where
 * they get combined for lookup.
 */
export class MappingsRegistry {
  private readonly bySlug: Map<string, Destination>

  private constructor(bySlug: Map<string, Destination>) {
    this.bySlug = bySlug
  }

  /** Build a registry from an array of validated destinations. */
  static from(destinations: Destination[]): MappingsRegistry {
    const bySlug = new Map<string, Destination>()
    for (const destination of destinations) {
      bySlug.set(combinedSlug(destination), destination)
    }
    return new MappingsRegistry(bySlug)
  }

  /** Look up a destination by combined slug (`<slug>-<version>`). */
  get(slug: string): Destination | undefined {
    return this.bySlug.get(slug)
  }

  has(slug: string): boolean {
    return this.bySlug.has(slug)
  }

  size(): number {
    return this.bySlug.size
  }

  /** List all known combined slugs (useful for diagnostics). */
  slugs(): string[] {
    return Array.from(this.bySlug.keys())
  }
}

/**
 * Compute the combined slug used as the registry key. Exported so the
 * loader can build the same key when merging bundled fallbacks.
 */
export function combinedSlug(d: Pick<Destination, 'slug' | 'version'>): string {
  return `${d.slug}-${d.version}`
}
